import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoComplete, Button, Modal, Input, Table, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import { toast } from 'react-toastify';
import Fuse from 'fuse.js';

import { BiExport } from 'react-icons/bi';
import { FaEye, FaPen, FaPlus, FaSearch } from 'react-icons/fa';
import { FaBoxArchive, FaBoxOpen } from "react-icons/fa6";
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';
import ExportOptionsModal from '../ExportOptionsModal';
import { DateRangeFilter, isInDateRange } from '../../../utils/tableUtils';

import 'react-toastify/dist/ReactToastify.css';
import '../../../css/UserAdmin/Global.css';
import '../../../css/UserAdmin/ManageSubjectsPage.css';

dayjs.extend(utc);
dayjs.extend(isBetween);

const ALL_EXPORT_COLUMNS = [
    { label: 'Subject Order', value: 'subjectOrder' },
    { label: 'Subject No.', value: 'subjectID' },
    { label: 'Subject Code', value: 'subjectCode' },
    { label: 'Subject Name', value: 'subjectName' },
    { label: 'Written Work %', value: 'writtenWork' },
    { label: 'Performance Task %', value: 'performanceTask' },
    { label: 'Quarterly Assessment %', value: 'quarterlyAssessment' },
    { label: 'Classification', value: 'classification' },
    { label: 'Strand', value: 'strand' },
    { label: 'Term', value: 'term' },
    { label: 'Grade Level', value: 'gradeLevel' },
    { label: 'Status', value: 'status' },
    { label: 'Created At', value: 'createdAt' },
    { label: 'Updated At', value: 'updatedAt' },
];

const ManageSubjectsPage = () => {
    const navigate = useNavigate();
    // State for layout and table
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const [dataSource, setDataSource] = useState([]);
    const [rawApiData, setRawApiData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [tableFilters, setTableFilters] = useState({});
    const [sorter, setSorter] = useState({});
    const [showArchived, setShowArchived] = useState(false);
    const [autocompleteOptions, setAutocompleteOptions] = useState([]);

    // State for the export functionality
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const debounce = (func, delay) => { let timeout; return function (...args) { const context = this; clearTimeout(timeout); timeout = setTimeout(() => func.apply(context, args), delay); }; };

    const applyFuzzySearchAndSetDataSource = useCallback((dataToFilter, currentSearchTerm) => {
        if (!currentSearchTerm.trim()) {
            setDataSource(dataToFilter.map(item => ({ ...item, key: item._id })));
            return;
        }
        const dataWithSearchableDates = dataToFilter.map(item => ({ ...item, writtenWorkStr: String(item.writtenWork), performanceTaskStr: String(item.performanceTask), quarterlyAssessmentStr: String(item.quarterlyAssessment), search_createdAt: dayjs(item.createdAt).isValid() ? dayjs(item.createdAt).format('MMM D, YYYY h:mm A').toLowerCase() : '', search_updatedAt: dayjs(item.updatedAt).isValid() ? dayjs(item.updatedAt).format('MMM D, YYYY h:mm A').toLowerCase() : '' }));
        const fuse = new Fuse(dataWithSearchableDates, { includeScore: true, threshold: 0.4, keys: ['subjectID', 'subjectCode', 'subjectName', 'classification', 'strand', 'term', 'gradeLevel', 'status', 'writtenWorkStr', 'performanceTaskStr', 'quarterlyAssessmentStr', 'search_createdAt', 'search_updatedAt'] });
        setDataSource(fuse.search(currentSearchTerm).map(result => ({ ...result.item, key: result.item._id })));
    }, []);

    const fetchSubjectsData = useCallback(async (currentShowArchived) => {
        setLoading(true);
        try {
            const apiUrl = `${process.env.REACT_APP_API_URL}/api/admin/subjects?archived=${currentShowArchived}`;
            const response = await fetch(apiUrl);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const result = await response.json();
            const apiData = Array.isArray(result.data) ? result.data : [];
            setRawApiData(apiData);
            applyFuzzySearchAndSetDataSource(apiData, searchTerm);
        } catch (error) {
            toast.error(`Error fetching subjects: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, applyFuzzySearchAndSetDataSource]);

    useEffect(() => { fetchSubjectsData(showArchived); }, [showArchived, fetchSubjectsData]);
    useEffect(() => { const debouncedSearch = debounce(() => applyFuzzySearchAndSetDataSource(rawApiData, searchTerm), 300); debouncedSearch(); return () => clearTimeout(debouncedSearch); }, [searchTerm, rawApiData, applyFuzzySearchAndSetDataSource]);

    const handleSearchInputChange = (value) => setSearchTerm(value || '');
    const handleAutocompleteSuggest = (searchText) => { if (!searchText) { setAutocompleteOptions([]); return; } const lowerSearchText = searchText.toLowerCase(); const suggestions = rawApiData.flatMap(item => [item.subjectID, item.subjectCode, item.subjectName, item.classification, item.strand, item.term, item.gradeLevel]).filter(Boolean).filter((value, index, self) => self.map(v => String(v).toLowerCase()).indexOf(String(value).toLowerCase()) === index).filter(value => String(value).toLowerCase().includes(lowerSearchText)).map(value => ({ value: String(value), label: String(value) })); setAutocompleteOptions(suggestions.slice(0, 10)); };
    const handleClearFilters = () => { setTableFilters({}); setSorter({}); setSearchTerm(''); setAutocompleteOptions([]); fetchSubjectsData(showArchived); };

    const handleFinishExport = async (values) => {
        setIsExporting(true);
        try {
            const { format, columns, dateRange, classifications, strands, terms, gradeLevels, statuses } = values;

            const fullName = localStorage.getItem('fullName') || 'Unknown User';
            const role = localStorage.getItem('role') || 'Unknown Role';
            const userID = localStorage.getItem('userID') || 'Unknown ID';

            const endpoint = format === 'pdf' ? `${process.env.REACT_APP_API_URL}/api/admin/export/pdf/subjects` : `${process.env.REACT_APP_API_URL}/api/admin/export/csv/subjects`;
            const params = new URLSearchParams({ userID, fullName, role, columns: columns.join(','), isArchived: showArchived });

            if (dateRange && dateRange.length === 2) { params.append('startDate', dateRange[0].toISOString()); params.append('endDate', dateRange[1].toISOString()); }
            if (classifications && classifications.length > 0) { classifications.forEach(c => params.append('classifications', c)); }
            if (strands && strands.length > 0) { strands.forEach(s => params.append('strands', s)); }
            if (terms && terms.length > 0) { terms.forEach(t => params.append('terms', t)); }
            if (gradeLevels && gradeLevels.length > 0) { gradeLevels.forEach(gl => params.append('gradeLevels', gl)); }
            if (statuses && statuses.length > 0) { statuses.forEach(s => params.append('statuses', s)); }
            if (searchTerm) { params.append('searchText', searchTerm); }

            const requestUrl = `${endpoint}?${params.toString()}`;
            const response = await fetch(requestUrl);
            if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);

            const blob = await response.blob();
            const fileName = `subjects-report-${dayjs().format('YYYY-MM-DD')}.${format}`;

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setIsExportModalOpen(false);
            toast.success("Report generated successfully!");

            const logData = { userID, accountName: fullName, role, action: 'Export', detail: `Exported subjects report: ${fileName}` };
            fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(logData)
            }).then(res => res.json()).then(data => console.log('System log recorded:', data))
                .catch(err => console.error('Failed to record system log:', err));
        } catch (error) {
            console.error(`Export to ${values.format.toUpperCase()} failed:`, error);
            Modal.error({ title: 'Export Failed', content: `Could not generate the report.` });
        } finally {
            setIsExporting(false);
        }
    };

    const handleCreate = () => navigate('/admin/manage-subjects/create');
    const handleArchiveToggle = async (record) => {
        const { _id, isArchived } = record;

        const actionType = isArchived ? 'Unarchive' : 'Archive';
        console.log(`Attempting to ${actionType.toLowerCase()} subject: ${_id}`);

        try {
            const subjectRes = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/subjects/${_id}`);
            if (!subjectRes.ok) throw new Error('Failed to fetch subject details');
            const subject = await subjectRes.json();
            const { subjectName } = subject.data || {};

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/subjects/archive/${_id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isArchived: !isArchived })
            });
            if (!response.ok) throw new Error(`Failed to ${actionType.toLowerCase()} subject`);
            toast.success(`Subject ${actionType.toLowerCase()}d successfully!`);

            const adminID = localStorage.getItem('userID');
            const adminName = localStorage.getItem('fullName');
            const adminRole = localStorage.getItem('role');
            const logDetail = `${actionType}d subject ${subjectName || 'N/A'} [ID: ${_id}]`;
            const logData = { userID: adminID, accountName: adminName, role: adminRole, action: actionType, detail: logDetail };
            await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(logData)
            });
            fetchSubjectsData(showArchived);
        } catch (error) {
            console.error(`${actionType} error:`, error);
            toast.error(`Error ${actionType.toLowerCase()}ing subject: ${error.message}`);
        }
    };

    const columns = [
        { title: 'Order', dataIndex: 'subjectOrder', key: 'subjectOrder', width: 80, fixed: 'left', sorter: (a, b) => (a.subjectOrder || 0) - (b.subjectOrder || 0), sortOrder: sorter.columnKey === 'subjectOrder' ? sorter.order : null },
        { title: 'Code', dataIndex: 'subjectCode', key: 'subjectCode', width: 120, sorter: (a, b) => (a.subjectCode || '').localeCompare(b.subjectCode || ''), sortOrder: sorter.columnKey === 'subjectCode' ? sorter.order : null },
        { title: 'Name', dataIndex: 'subjectName', key: 'subjectName', width: 250, sorter: (a, b) => (a.subjectName || '').localeCompare(b.subjectName || ''), sortOrder: sorter.columnKey === 'subjectName' ? sorter.order : null },
        { title: 'WW', dataIndex: 'writtenWork', key: 'writtenWork', width: 70, render: (text) => `${text}%` },
        { title: 'PT', dataIndex: 'performanceTask', key: 'performanceTask', width: 70, render: (text) => `${text}%` },
        { title: 'QA', dataIndex: 'quarterlyAssessment', key: 'quarterlyAssessment', width: 70, render: (text) => `${text}%` },
        { title: 'Classification', dataIndex: 'classification', key: 'classification', width: 130, filters: [{ text: 'CORE', value: 'CORE' }, { text: 'APPLIED', value: 'APPLIED' }, { text: 'STRAND', value: 'STRAND' }, { text: 'CVF', value: 'CVF' }], onFilter: (value, record) => (record.classification || '').includes(value), filteredValue: tableFilters.classification || null },
        { title: 'Strand', dataIndex: 'strand', key: 'strand', width: 100, filters: [{ text: 'N/A', value: 'N/A' }, { text: 'STEM', value: 'STEM' }, { text: 'ABM', value: 'ABM' }, { text: 'HUMSS', value: 'HUMSS' }, { text: 'TVL', value: 'TVL' }], onFilter: (value, record) => (record.strand || '').includes(value), filteredValue: tableFilters.strand || null },
        { title: 'Term', dataIndex: 'term', key: 'term', width: 80, filters: [{ text: '1st', value: '1st' }, { text: '2nd', value: '2nd' }, { text: 'Whole Sem', value: 'Whole Sem' }], onFilter: (value, record) => (record.term || '').includes(value), filteredValue: tableFilters.term || null },
        { title: 'Grade Level', dataIndex: 'gradeLevel', key: 'gradeLevel', width: 120, filters: [{ text: 'Grade 11', value: 'Grade 11' }, { text: 'Grade 12', value: 'Grade 12' }], onFilter: (value, record) => (record.gradeLevel || '').includes(value), filteredValue: tableFilters.gradeLevel || null },
        { title: 'Status', dataIndex: 'status', key: 'status', width: 100, filters: [{ text: 'Active', value: 'Active' }, { text: 'Inactive', value: 'Inactive' }], onFilter: (value, record) => (record.status || '').includes(value), filteredValue: tableFilters.status || null, render: (status) => (<Tag color={status === 'Active' ? 'green' : 'volcano'}>{status}</Tag>) },
        { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt', width: 180, sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(), sortOrder: sorter.columnKey === 'createdAt' ? sorter.order : null, filterDropdown: (props) => <DateRangeFilter {...props} />, onFilter: (value, record) => isInDateRange(value, record.createdAt), filteredValue: tableFilters.createdAt || null, render: (text) => dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : 'Invalid Date' },
        { title: 'Updated At', dataIndex: 'updatedAt', key: 'updatedAt', width: 180, sorter: (a, b) => dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix(), sortOrder: sorter.columnKey === 'updatedAt' ? sorter.order : null, filterDropdown: (props) => <DateRangeFilter {...props} />, onFilter: (value, record) => isInDateRange(value, record.updatedAt), filteredValue: tableFilters.updatedAt || null, render: (text) => dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : 'Invalid Date' },
        {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: 170, // Adjusted
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                    <Button
                        icon={record.isArchived ? <FaEye /> : <FaPen />}
                        style={{ width: '130px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                        onClick={() => navigate(`/admin/manage-subjects/edit/${record.key}`)}
                    >
                        {record.isArchived ? 'View' : 'Edit'}
                    </Button>
                    <Tooltip
                        title={
                            (!record.isArchived && record.status === 'Active')
                                ? 'Cannot archive active subjects'
                                : (record.isArchived ? 'Unarchive this subject' : 'Archive this subject')
                        }
                    >
                        <span style={{ display: 'inline-block', width: '130px' }}>
                            <Button
                                icon={record.isArchived ? <FaBoxOpen /> : <FaBoxArchive />}
                                style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                onClick={() => handleArchiveToggle(record)}
                                disabled={!record.isArchived && record.status === 'Active'}
                            >
                                {record.isArchived ? 'Unarchive' : 'Archive'}
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            )
        }
    ];

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            {showArchived && (<Tag className="archived-tag" color="volcano">Viewing <strong>Archived Subjects</strong></Tag>)}
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container"><SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} /></div>
                <main className="admin-main-content-area">
                    <div className="page-title"><p className="heading">{showArchived ? "Archived Subjects" : "Manage Subjects"}</p></div>
                    <div className="table-functions">
                        <div className="left-tools">
                            <Button icon={<FiFilter />} onClick={handleClearFilters}>Clear Filter</Button>
                            <Button icon={<HiOutlineRefresh />} onClick={() => fetchSubjectsData(showArchived)}>Refresh</Button>
                            <Button icon={<BiExport />} onClick={() => setIsExportModalOpen(true)}>Export</Button>
                        </div>
                        <div className="right-tools">
                            <AutoComplete value={searchTerm} options={autocompleteOptions} style={{ width: 300 }} onSearch={handleAutocompleteSuggest} onChange={handleSearchInputChange} onSelect={handleSearchInputChange}>
                                <Input placeholder="Search Subjects..." allowClear suffix={<FaSearch style={{ color: '#aaa' }} />} />
                            </AutoComplete>
                            <Button icon={showArchived ? <FaBoxOpen /> : <FaBoxArchive />} onClick={() => setShowArchived(prev => !prev)}>{showArchived ? 'Close Archive' : 'Show Archive'}</Button>
                            <Button type="ghost" className="create-btn" icon={<FaPlus />} onClick={handleCreate}>Create Subject</Button>
                        </div>
                    </div>
                    <Table style={{ width: '100%', flex: 1 }} columns={columns} dataSource={dataSource} loading={loading} scroll={{ x: 'max-content' }} pagination bordered onChange={(p, f, s) => { setTableFilters(f); setSorter(s); }} />
                </main>
            </div>
            <Footer />
            <ExportOptionsModal
                visible={isExportModalOpen}
                onCancel={() => setIsExportModalOpen(false)}
                onExport={handleFinishExport}
                isLoading={isExporting}
                exportColumns={ALL_EXPORT_COLUMNS}
                // The textFilter prop is omitted to use the main search bar's value
                filterOptions={[
                    { key: 'classifications', name: 'classifications', label: 'Classification', options: columns.find(c => c.key === 'classification')?.filters || [] },
                    { key: 'strands', name: 'strands', label: 'Strand', options: columns.find(c => c.key === 'strand')?.filters || [] },
                    { key: 'terms', name: 'terms', label: 'Term', options: columns.find(c => c.key === 'term')?.filters || [] },
                    { key: 'gradeLevels', name: 'gradeLevels', label: 'Grade Level', options: columns.find(c => c.key === 'gradeLevel')?.filters || [] },
                    { key: 'statuses', name: 'statuses', label: 'Status', options: columns.find(c => c.key === 'status')?.filters || [] },
                ]}
            />
        </div>
    );
};

export default ManageSubjectsPage;